import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import { FiMapPin, FiPhone, FiMail, FiClock, FiDollarSign, FiSmartphone } from 'react-icons/fi';
import { motion } from 'framer-motion';

const Contact = () => {
  const { t, language } = useLanguage();
  const qrUrl = "https://media-manager-c.questera.ai/greta-media/c4cd7048ee42a7b0632cdf5aa1fa307c48d20f8ae20304e7af2c62d9e8bb8b361540d414afb44516657958e65ec582f9/images/aW1hZ2UvcG5n/4cccfcea5b12263d4cf57505131a66d2.png";

  const contactInfo = [
    { 
      icon: FiMapPin, 
      title: language === 'es' ? 'Dirección' : 'Address', 
      detail: t('contact.info.address') 
    },
    { 
      icon: FiPhone, 
      title: language === 'es' ? 'Teléfono' : 'Phone', 
      detail: t('contact.info.phone'),
      href: 'tel:6616774253'
    },
    { 
      icon: FiMail, 
      title: language === 'es' ? 'Correo' : 'Email', 
      detail: t('contact.info.email'),
      href: 'mailto:Alberto.406@hotmail.com'
    },
    { 
      icon: FiDollarSign, 
      title: 'Zelle', 
      detail: t('contact.info.zelle'),
      isZelle: true
    },
    { 
      icon: FiClock, 
      title: language === 'es' ? 'Horario' : 'Hours', 
      detail: t('contact.info.hours') 
    },
  ];

  return (
    <section id="contact" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('contact.title')}</h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-6 rounded-full"></div>
          <p className="text-lg text-gray-600">{t('contact.subtitle')}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            {contactInfo.map((info, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`flex items-start p-4 rounded-xl transition-colors ${info.isZelle ? 'bg-purple-50 border border-purple-100' : ''}`}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center shadow-sm border ${info.isZelle ? 'bg-purple-600 border-purple-500' : 'bg-white border-gray-100'}`}>
                  <SafeIcon icon={info.icon} className={`w-5 h-5 ${info.isZelle ? 'text-white' : 'text-blue-600'}`} />
                </div>
                <div className="ml-4">
                  <h4 className={`text-sm font-bold uppercase tracking-wider ${info.isZelle ? 'text-purple-900' : 'text-gray-500'}`}>{info.title}</h4>
                  {info.href ? (
                    <a href={info.href} className="mt-1 text-gray-900 hover:text-blue-600 transition-colors block font-semibold text-lg">
                      {info.detail}
                    </a>
                  ) : (
                    <p className={`mt-1 text-gray-900 font-semibold text-lg whitespace-pre-line ${info.isZelle ? 'text-purple-700' : ''}`}>{info.detail}</p>
                  )}
                </div>
              </motion.div>
            ))}

            {/* Optimized QR Block */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-[2rem] shadow-xl border border-blue-100 flex flex-col items-center text-center w-full"
            >
              <div className="flex items-center gap-2 mb-6 text-blue-600">
                <SafeIcon icon={FiSmartphone} className="w-5 h-5" />
                <span className="font-bold uppercase tracking-widest text-xs">{t('services.taxes.scanText')}</span>
              </div>
              <div className="bg-gray-50 p-6 rounded-2xl border-2 border-dashed border-blue-200 mb-6 flex justify-center items-center w-full">
                <img src={qrUrl} alt="Custom QR" className="w-48 h-48 object-contain mix-blend-multiply" />
              </div>
              <div className="space-y-1">
                <p className="text-gray-900 font-bold text-sm">
                  {language === 'es' ? 'Portal de Taxes' : 'Tax Portal'}
                </p>
                <p className="text-gray-500 text-xs leading-relaxed max-w-[200px]">
                  {language === 'es' 
                    ? 'Abre tu cámara y escanea para iniciar tu proceso.' 
                    : 'Open your camera and scan to start your process.'}
                </p>
              </div>
            </motion.div>
          </div>

          {/* Contact Form */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2 bg-white rounded-2xl shadow-xl p-8 border border-gray-100 h-fit"
          >
            <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">{t('contact.form.name')}</label>
                  <input type="text" id="name" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">{t('contact.form.email')}</label>
                  <input type="email" id="email" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all" />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">{t('contact.form.phone')}</label>
                  <input type="tel" id="phone" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all" />
                </div>
                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">{t('contact.form.service')}</label>
                  <select id="service" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all bg-white">
                    <option value="immigration">{t('services.immigration.title')}</option>
                    <option value="taxes">{t('services.taxes.title')}</option>
                    <option value="other">{language === 'es' ? 'Otro' : 'Other'}</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">{t('contact.form.message')}</label>
                <textarea id="message" rows="4" className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all resize-none"></textarea>
              </div>

              <button type="submit" className="w-full bg-blue-600 text-white font-bold py-4 rounded-lg hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl">
                {t('contact.form.submit')}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;